import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  LogOut, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Home, 
  Building2,
  Bath,
  BedDouble,
  MessageSquare,
  Search,
  RefreshCw,
  ClipboardList
} from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = checking, false = not auth, object = auth
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");

  // Check if already authenticated via cookies
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include'
      });
      if (response.ok) {
        const user = await response.json();
        setIsAuthenticated(user);
        fetchQuotes();
      } else {
        setIsAuthenticated(false);
      }
    } catch (error) {
      setIsAuthenticated(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password })
      });
      
      if (response.ok) {
        const user = await response.json();
        setIsAuthenticated(user);
        fetchQuotes();
        toast.success("Welcome to the Admin Dashboard");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Login failed");
      }
    } catch (error) {
      toast.error("Connection error. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      // Log but continue - logout should proceed locally even if API fails
      console.warn('Logout API call failed:', error.message);
    }
    setIsAuthenticated(false);
    navigate("/");
  };

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        const sorted = data.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        );
        setQuotes(sorted);
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        toast.error("Session expired. Please login again.");
      }
    } catch (error) {
      toast.error("Failed to fetch quotes");
    } finally {
      setLoading(false);
    }
  };

  const updateQuoteStatus = async (quoteId, newStatus) => {
    try {
      const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus }),
      });
      if (response.ok) {
        setQuotes(quotes.map(q => 
          q.id === quoteId ? { ...q, status: newStatus } : q
        ));
        toast.success(`Quote marked as ${newStatus}`);
      } else if (response.status === 401) {
        setIsAuthenticated(false);
        toast.error("Session expired. Please login again.");
      }
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const filteredQuotes = quotes.filter(quote => {
    const matchesSearch = 
      quote.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quote.postcode.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === "all" || quote.status === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case "pending": return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "contacted": return "bg-blue-100 text-blue-800 border-blue-200";
      case "completed": return "bg-green-100 text-green-800 border-green-200";
      case "cancelled": return "bg-red-100 text-red-800 border-red-200";
      default: return "bg-slate-100 text-slate-800 border-slate-200";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Loading state while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#3B82F6]" />
      </div>
    );
  }

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800/50 backdrop-blur-xl">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_0e17f384-87af-4f21-afc9-222d2d083a19/artifacts/el8zbrgy_cb05fdee-8c6a-4b70-bf8c-a5cc72353916.jpeg"
                alt="DCS Logo"
                className="h-20 w-20 rounded-xl mx-auto"
              />
            </div>
            <CardTitle className="text-2xl font-bold text-white" style={{ fontFamily: "Manrope, sans-serif" }}>
              Admin Dashboard
            </CardTitle>
            <p className="text-slate-400 text-sm mt-2">
              Sign in to access quote management
            </p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <Input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="admin-email-input"
                required
              />
              <Input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="bg-slate-700/50 border-slate-600 text-white placeholder:text-slate-400"
                data-testid="admin-password-input"
                required
              />
              <Button 
                type="submit" 
                className="w-full bg-[#3B82F6] hover:bg-[#2563EB]"
                data-testid="admin-login-btn"
                disabled={loginLoading}
              >
                {loginLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  "Sign In"
                )}
              </Button>
              <Button 
                type="button"
                variant="ghost" 
                className="w-full text-slate-400 hover:text-white"
                onClick={() => navigate("/")}
              >
                Back to Website
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dashboard Screen
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-[#0F172A] text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_0e17f384-87af-4f21-afc9-222d2d083a19/artifacts/el8zbrgy_cb05fdee-8c6a-4b70-bf8c-a5cc72353916.jpeg"
                alt="DCS Logo"
                className="h-12 w-12 rounded-lg"
              />
              <div>
                <h1 className="font-bold text-lg" style={{ fontFamily: "Manrope, sans-serif" }}>
                  Quote Management
                </h1>
                <p className="text-slate-400 text-sm">Detailed Cleaning Services</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchQuotes}
                disabled={loading}
                className="text-slate-300 hover:text-white"
                data-testid="refresh-quotes-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="text-slate-300 hover:text-white"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Quotes</p>
                  <p className="text-2xl font-bold text-slate-900">{quotes.length}</p>
                </div>
                <ClipboardList className="w-8 h-8 text-[#3B82F6]" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {quotes.filter(q => q.status === "pending").length}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-yellow-100 flex items-center justify-center">
                  <span className="text-yellow-600 font-bold">!</span>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Contacted</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {quotes.filter(q => q.status === "contacted").length}
                  </p>
                </div>
                <Phone className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Completed</p>
                  <p className="text-2xl font-bold text-green-600">
                    {quotes.filter(q => q.status === "completed").length}
                  </p>
                </div>
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                  <span className="text-green-600 font-bold">✓</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search by name, email, or postcode..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-quotes-input"
            />
          </div>
          <div className="flex gap-2">
            {["all", "pending", "contacted", "completed", "cancelled"].map((status) => (
              <Button
                key={status}
                variant={filterStatus === status ? "default" : "outline"}
                size="sm"
                onClick={() => setFilterStatus(status)}
                className={filterStatus === status ? "bg-[#3B82F6]" : ""}
                data-testid={`filter-${status}-btn`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </Button>
            ))}
          </div>
        </div>

        {/* Quotes List */}
        <div className="space-y-4">
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-[#3B82F6]" />
              <p className="text-slate-500 mt-2">Loading quotes...</p>
            </div>
          ) : filteredQuotes.length === 0 ? (
            <Card className="border-slate-200">
              <CardContent className="py-12 text-center">
                <ClipboardList className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No quotes found</p>
              </CardContent>
            </Card>
          ) : (
            filteredQuotes.map((quote) => (
              <Card 
                key={quote.id} 
                className="border-slate-200 hover:shadow-md transition-shadow"
                data-testid={`quote-card-${quote.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                    {/* Main Info */}
                    <div className="flex-1 space-y-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-bold text-lg text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
                            {quote.name}
                          </h3>
                          <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              <a href={`mailto:${quote.email}`} className="hover:text-[#3B82F6]">
                                {quote.email}
                              </a>
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {quote.postcode}
                            </span>
                          </div>
                        </div>
                        <Badge className={`${getStatusColor(quote.status)} border`}>
                          {quote.status}
                        </Badge>
                      </div>

                      {/* Details Grid */}
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 py-3 border-y border-slate-100">
                        <div className="flex items-center gap-2 text-sm">
                          <span className="text-[#3B82F6]">
                            {quote.property_type === "House" ? <Home className="w-4 h-4" /> : <Building2 className="w-4 h-4" />}
                          </span>
                          <span className="text-slate-600">{quote.property_type}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <BedDouble className="w-4 h-4 text-[#3B82F6]" />
                          <span className="text-slate-600">{quote.bedrooms || "N/A"} beds</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Bath className="w-4 h-4 text-[#3B82F6]" />
                          <span className="text-slate-600">{quote.bathrooms || "N/A"} baths</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-[#3B82F6]" />
                          <span className="text-slate-600">{formatDate(quote.created_at)}</span>
                        </div>
                      </div>

                      {/* Service & Message */}
                      <div className="space-y-2">
                        <Badge variant="outline" className="bg-[#3B82F6]/10 text-[#3B82F6] border-[#3B82F6]/20">
                          {quote.service_type}
                        </Badge>
                        {quote.message && (
                          <div className="flex items-start gap-2 text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">
                            <MessageSquare className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                            <p>{quote.message}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex lg:flex-col gap-2 lg:min-w-[140px]">
                      <Button
                        size="sm"
                        variant={quote.status === "contacted" ? "default" : "outline"}
                        className={quote.status === "contacted" ? "bg-blue-500" : ""}
                        onClick={() => updateQuoteStatus(quote.id, "contacted")}
                        data-testid={`mark-contacted-${quote.id}`}
                      >
                        <Phone className="w-4 h-4 mr-1" />
                        Contacted
                      </Button>
                      <Button
                        size="sm"
                        variant={quote.status === "completed" ? "default" : "outline"}
                        className={quote.status === "completed" ? "bg-green-500" : ""}
                        onClick={() => updateQuoteStatus(quote.id, "completed")}
                        data-testid={`mark-completed-${quote.id}`}
                      >
                        Completed
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        onClick={() => updateQuoteStatus(quote.id, "cancelled")}
                        data-testid={`mark-cancelled-${quote.id}`}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
